import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GET, POST, PUT, DELETE } from '../../services/api';

// Define the async thunk for adding a podcast
export const addPodcast = createAsyncThunk(
  'data/add/podcast',
  async (data, { rejectWithValue }) => {
    try {
      const response = await POST('/admin/podcast', data);
      if (response?.status === 200) {
        return response.data.result;
      } else {
        return rejectWithValue(response?.data || 'Something went wrong');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data || 'Failed to add podcast');
    }
  }
);

// Define the async thunk for viewing a specific podcast
export const viewPodcast = createAsyncThunk(
  'data/view/podcast',
  async (podcastId, { rejectWithValue }) => {
    try {
      const response = await GET(`/admin/podcast/view`, { podcast_id: podcastId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || 'An error occurred');
    }
  }
);

export const viewPodcastActivity = createAsyncThunk(
  'data/view/podcast/activity',
  async (podcastId, { rejectWithValue }) => { // Explicitly type podcastId
    try {
      const response = await GET(`/admin/podcast/activity`, { podcast_id: podcastId });
      if (response && response.data) {
        return response.data;
      } else {
        return rejectWithValue('Failed to fetch podcast activity: Empty response'); // More specific error
      }
    } catch (error) { // Type the error as any or AxiosError
      return rejectWithValue(error?.response?.data?.message || 'Failed to fetch podcast activity'); //Get message from response
    }
  }
);


// Define the async thunk for listing all podcasts
export const listPodcasts = createAsyncThunk(
  'data/list/podcasts',
  async (params={}, { rejectWithValue }) => {
    try {
      const response = await GET('/admin/podcast', {...params});
      if (response?.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response?.data || 'Something went wrong');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data || 'Failed to fetch podcasts');
    }
  }
);


export const DefaultPodcast = createAsyncThunk(
  'data/default/podcasts',
  async (params={}, { rejectWithValue }) => {
    try {
      const response = await GET('/admin/podcast/default', {...params});
      if (response?.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response?.data || 'Something went wrong');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data || 'Failed to fetch podcasts');
    }
  }
);



// Define the async thunk for updating a podcast
export const updatePodcast = createAsyncThunk(
  'data/update/podcast',
  async ({ id, data,  }, { rejectWithValue }) => {
    try {
      const response = await PUT(`/admin/podcast?podcast_id=${id}`, {...data});
      if (response?.status === 200) {
        return response.data.result;
      } else {
        return rejectWithValue(response?.data || 'Something went wrong');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data || 'Failed to update podcast');
    }
  }
);

export const updateDefaultPodcast = createAsyncThunk(
  'data/update/podcast/default',
  async ({ id, file_url_en,  }, { rejectWithValue }) => {
    try {
      const response = await PUT(`/admin/podcast/default?id=${id}`, {file_url_en});
      if (response?.status === 200) {
        return response.data.result;
      } else {
        return rejectWithValue(response?.data || 'Something went wrong');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data || 'Failed to update podcast');
    }
  }
);


// Define the async thunk for deleting a podcast
export const deletePodcast = createAsyncThunk(
  'data/delete/podcast',
  async (id, { rejectWithValue }) => {
    try {
      const response = await DELETE(`/admin/podcast`, { podcast_id: id });
      if (response?.status === 200) {
        return response.data.result;
      } else {
        return rejectWithValue(response?.data || 'Something went wrong');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data || 'Failed to delete podcast');
    }
  }
);

const ManagePodcastSlice = createSlice({
  name: 'managePodcast',
  initialState: {
    addPodcastData: [],
    addPodcastLoading: false,
    addPodcastError: null,
    listPodcastsData: [],
    listPodcastsCount: '',
    listPodcastsLoading: false,
    listPodcastsError: null,

    DefPodcastsData: [],
    DefPodcastsLoading: false,
    DefPodcastsError: null,

    viewPodcastData: [],
    viewPodcastLoading: false,
    viewPodcastError: null,

    viewPodcastActivityData: [],
    viewPodcastActivityLoading: false,
    viewPodcastActivityError: null,


    updatePodcastData: [],
    updatePodcastLoading: false,
    updatePodcastError: null,
    updateDefPodcastData: [],
    updateDefPodcastLoading: false,
    updateDefPodcastError: null,
    deletePodcastData: [],
    deletePodcastLoading: false,
    deletePodcastError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addPodcast.pending, (state) => {
        state.addPodcastLoading = true;
        state.addPodcastError = null;
      })
      .addCase(addPodcast.fulfilled, (state, action) => {
        state.addPodcastLoading = false;
        state.addPodcastData = action.payload;
      })
      .addCase(addPodcast.rejected, (state, action) => {
        state.addPodcastLoading = false;
        state.addPodcastError = action.error.message;
      })


      .addCase(viewPodcast.pending, (state) => {
        state.viewPodcastLoading = true;
        state.viewPodcastError = null;
      })
      .addCase(viewPodcast.fulfilled, (state, action) => {
        state.viewPodcastLoading = false;
        state.viewPodcastData = action.payload.result;
      })
      .addCase(viewPodcast.rejected, (state, action) => {
        state.viewPodcastLoading = false;
        state.viewPodcastError = action.error.message;
      })

      .addCase(viewPodcastActivity.pending, (state) => {
        state.viewPodcastActivityLoading = true;
        state.viewPodcastActivityError = null;
      })
      .addCase(viewPodcastActivity.fulfilled, (state, action) => {
        state.viewPodcastActivityLoading = false;
        state.viewPodcastActivityData = action?.payload?.result?.rows;
      })
      .addCase(viewPodcastActivity.rejected, (state, action) => {
        state.viewPodcastActivityLoading = false;
        state.viewPodcastActivityError = action.error.message;
      })


      .addCase(listPodcasts.pending, (state) => {
        state.listPodcastsLoading = true;
        state.listPodcastsError = null;
      })
      .addCase(listPodcasts.fulfilled, (state, action) => {
        state.listPodcastsLoading = false;
        state.listPodcastsData = action?.payload?.result?.rows;
        state.listPodcastsCount = action?.payload?.result?.count;
      })
      .addCase(listPodcasts.rejected, (state, action) => {
        state.listPodcastsLoading = false;
        state.listPodcastsError = action.error.message;
      })

      .addCase(DefaultPodcast.pending, (state) => {
        state.DefPodcastsLoading = true;
        state.DefPodcastsError = null;
      })
      .addCase(DefaultPodcast.fulfilled, (state, action) => {
        state.DefPodcastsLoading = false;
        state.DefPodcastsData = action?.payload?.result;
       
      })
      .addCase(DefaultPodcast.rejected, (state, action) => {
        state.DefPodcastsLoading = false;
        state.DefPodcastsError = action.error.message;
      })

      .addCase(updatePodcast.pending, (state) => {
        state.updatePodcastLoading = true;
        state.updatePodcastError = null;
      })
      .addCase(updatePodcast.fulfilled, (state, action) => {
        state.updatePodcastLoading = false;
        state.updatePodcastData = action.payload;
      })
      .addCase(updatePodcast.rejected, (state, action) => {
        state.updatePodcastLoading = false;
        state.updatePodcastError = action.error.message;
      })

      .addCase(updateDefaultPodcast.pending, (state) => {
        state.updateDefPodcastLoading = true;
        state.updateDefPodcastError = null;
      })
      .addCase(updateDefaultPodcast.fulfilled, (state, action) => {
        state.updateDefPodcastLoading = false;
        state.updateDefPodcastData = action.payload;
      })
      .addCase(updateDefaultPodcast.rejected, (state, action) => {
        state.updateDefPodcastLoading = false;
        state.updateDefPodcastError = action.error.message;
      })

      .addCase(deletePodcast.pending, (state) => {
        state.deletePodcastLoading = true;
        state.deletePodcastError = null;
      })
      .addCase(deletePodcast.fulfilled, (state, action) => {
        state.deletePodcastLoading = false;
        state.deletePodcastData = action.payload;
      })
      .addCase(deletePodcast.rejected, (state, action) => {
        state.deletePodcastLoading = false;
        state.deletePodcastError = action.error.message;
      });
  },
});

export default ManagePodcastSlice.reducer;
